import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import "./CarrierSearch.css";
import { Outlet } from "react-router-dom";
import Box from "@mui/material/Box";
import CarrierSearchFilters from "./CarrierSearchFilters/CarrierSearchFilters";
import CarrierSearchOrderLevel from "./CarrierSearchOrderLevel/CarrierSearchOrderLevel";
import CarrierSearchHomeLocation from "./CarrierSearchHomeLocation/CarrierSearchHomeLocation";
import PrimaryTabs from "../TabPanels/PrimaryTabPanel/PrimaryTabs/PrimaryTabs";
import PrimaryTabPanel from "../TabPanels/PrimaryTabPanel/PrimaryTabPanel";
import PrimarySelect from "../Inputs/PrimarySelect/PrimarySelect";
import PrimaryTextField from "../Inputs/PrimaryTextField/PrimaryTextField";
import SecondaryButton from "../Buttons/SecondaryButton/SecondaryButton";
import { CarrierService } from "../../services/CarrierService";
import { ActiveUserContext, AlertContext } from "../Dashboard/Dashboard";

export default function CarrierSearch({
  handleTableAction
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [savedSearches, setSavedSearches] = useState([]);
  const [selectedSearch, setSelectedSearch] = useState("");
  const [searchName, setSearchName] = useState("");
  const [filters, setFilters] = useState([]);
  const [search, setSearch] = useState("");
  const [orderLevelResults, setOrderLevelResults] = useState([]);
  const [homeLocationResults, setHomeLocationResults] = useState([]);

  let tabLabels = useMemo(() => [
    {
      name: `Order Level (${orderLevelResults.length})`,
    },
    {
      name: `Home Location (${homeLocationResults.length})`,
    },
  ], [
    orderLevelResults,
    homeLocationResults
  ]);

  const handleSelectedSearchChanged = useCallback((value) => {
    setSearch("");

    const savedSearch = savedSearches.find(({ carrierSearchByUserId }) => carrierSearchByUserId === value);
    if (savedSearch) {
      setSearchName(savedSearch.carrierSearchName);
      setFilters(savedSearch.carrierSearchByUserFilters);
    } else {
      setSearchName("");
      setFilters([]);
      setOrderLevelResults([]);
      setHomeLocationResults([]);
    }
    setSelectedSearch(value);
  }, [savedSearches]);

  const handleSave = useCallback((name) => {
    let payload = {
      userObjectId: activeUser.userId,
      carrierSearchName: name,
      carrierSearchByUserFilters: filters,
      isActive: true
    };

    setIsLoading(true);
    CarrierService.CreateCarrierSavedSearch(instance, payload).then(res => {
      const savedSearchesUpdated = [...savedSearches];
      savedSearchesUpdated.push(res);
      setSavedSearches(savedSearchesUpdated);
      setSearchName(name);
      setSelectedSearch(res.carrierSearchByUserId);
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  }, [
    activeUser,
    filters
  ]);

  const handleUpdate = useCallback(() => {
    let payload = savedSearches.find(({ carrierSearchByUserId }) => carrierSearchByUserId === selectedSearch);
    if (payload) {
      payload.carrierSearchByUserFilters = filters;

      setIsLoading(true);
      CarrierService.UpdateCarrierSavedSearch(instance, payload).then(res => {
        const index = savedSearches.findIndex(({ carrierSearchByUserId }) => carrierSearchByUserId === selectedSearch);
        const savedSearchesUpdated = [...savedSearches];
        savedSearchesUpdated[index] = res;
        setSavedSearches(savedSearchesUpdated);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  }, [
    filters,
    savedSearches,
    selectedSearch
  ]);

  const handleDelete = useCallback(() => {
    let payload = savedSearches.find(({ carrierSearchByUserId }) => carrierSearchByUserId === selectedSearch);
    if (payload) {
      payload.isActive = false;

      setIsLoading(true);
      CarrierService.UpdateCarrierSavedSearch(instance, payload).then(() => {
        handleSelectedSearchChanged("");
        setIsLoading(false);
        getSavedSearches();
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  }, [
    handleSelectedSearchChanged,
    savedSearches,
    selectedSearch
  ]);

  const handleSearch = useCallback((customFilters = null) => {
    if (customFilters.length > 0 || filters.length > 0) {
      const params = {};
      if (customFilters.length > 0) {
        customFilters
          .filter(({ isQuery }) => isQuery)
          .forEach(filter => { params[filter.fieldName] = filter.params; });
      } else {
        filters
          .filter(({ isQuery }) => isQuery)
          .forEach(filter => { params[filter.fieldName] = filter.params; });
      }

      setIsLoading(true);
      setTimeout(() => {
        CarrierService.FindCarriersAdvanced(instance, params).then(res => {
          res.forEach(r => {
            r.active = r.carrierActive ? "Active" : "Inactive";
          });
          setOrderLevelResults(res);
          setIsLoading(false);
        }).catch(error => {
          console.error(error);
          setIsLoading(false);
          warning(error);
        });

        setIsLoading(true);
        CarrierService.HomeLocationSearch(instance, params).then(res => {
          res.forEach(r => {
            r.active = r.carrierActive ? "Active" : "Inactive";
          });
          setHomeLocationResults(res);
          setIsLoading(false);
        }).catch(error => {
          console.error(error);
          setIsLoading(false);
          warning(error);
        });
      }, [1]);
    }
  }, [filters]);

  const getSavedSearches = useCallback(() => {
    if (activeUser.userId) {
      setIsLoading(true);
      CarrierService.GetCarrierSavedSearchByUser(instance, activeUser.userId).then(res => {
        setSavedSearches(res.filter(({ isActive }) => isActive));
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  }, [activeUser]);

  useEffect(() => {
    getSavedSearches();
  }, [activeUser]);

  useEffect(() => {
    tabLabels[0].name = `Order Level (${orderLevelResults.length})`;
  }, [orderLevelResults]);

  useEffect(() => {
    tabLabels[1].name = `Home Location (${homeLocationResults.length})`;
  }, [homeLocationResults]);

  return (
    <Box>
      <CarrierSearchFilters
        isLoading={isLoading}
        filters={filters}
        setFilters={setFilters}
        selectedSearch={selectedSearch}
        handleSave={handleSave}
        handleSearch={handleSearch}
        handleUpdate={handleUpdate}
      />
      <Box className="search-container">
        <PrimaryTabs value={tabValue} handleChange={(e, v) => setTabValue(v)} tabLabels={tabLabels} />
        <Box sx={{ display: "flex", marginLeft: "auto", alignItems: "center" }}>
          {selectedSearch &&
            <Box sx={{ marginRight: "20px" }}>
              <SecondaryButton
                label={`Delete "${searchName}" search`}
                onClick={handleDelete}
                disabled={isLoading}
                size="small"
              />
            </Box>
          }
          {savedSearches.length > 0 &&
            <Box sx={{ marginRight: "10px" }}>
              <PrimarySelect
                defaultOption="Default Search"
                options={savedSearches}
                onChange={handleSelectedSearchChanged}
                value={selectedSearch}
                nameKey="carrierSearchName"
                valueKey="carrierSearchByUserId"
                width="130px"
                variant="standard"
                disabled={isLoading}
              />
            </Box>
          }
          <PrimaryTextField
            placeholder={`Search ${orderLevelResults.length} results`}
            onChange={setSearch}
            value={search}
            isSearch
          />
        </Box>
      </Box>
      <PrimaryTabPanel value={tabValue} index={0}>
        <CarrierSearchOrderLevel
          isLoading={isLoading}
          search={search}
          orderLevelResults={orderLevelResults}
          handleTableAction={handleTableAction}
        />
      </PrimaryTabPanel>
      <PrimaryTabPanel value={tabValue} index={1}>
        <CarrierSearchHomeLocation
          isLoading={isLoading}
          search={search}
          homeLocationResults={homeLocationResults}
          handleTableAction={handleTableAction}
        />
      </PrimaryTabPanel>
      <Outlet />
    </Box>
  );
}

CarrierSearch.propTypes = {
  handleTableAction: PropTypes.func
};